// src/features/tag-types/hooks/useTagTypeValidation.ts

import { useTranslate } from 'react-admin';
import { TagTypeFormData, TagTypeValidationErrors } from '../types';

export const useTagTypeValidation = () => {
  const translate = useTranslate();

  /**
   * Valida el campo 'Type Code'.
   * - Debe ser obligatorio.
   * - Longitud mínima.
   * - No debe contener espacios (solo letras, números, guiones bajos).
   */
  const validateTypeCode = (value?: string): string | undefined => {
    if (!value || value.trim().length === 0) {
      return translate('resources.tag_types.validation.type_code_required', 'El Type Code es obligatorio');
    }
    if (value.trim().length < 3) {
      return translate('resources.tag_types.validation.type_code_min_length', 'Debe tener al menos 3 caracteres');
    }
    if (/\s/.test(value)) {
        return translate('resources.tag_types.validation.type_code_no_spaces', 'No puede contener espacios');
    }
    if (!/^[A-Z0-9_]+$/.test(value)) {
        return translate('resources.tag_types.validation.type_code_format', 'Solo mayúsculas, números y guiones bajos');
    }
    return undefined;
  };

  /**
   * Valida el campo 'Display Name'.
   * - Debe ser obligatorio.
   * - Longitud mínima.
   */
  const validateName = (value?: string): string | undefined => {
    if (!value || value.trim().length === 0) {
      return translate('resources.tag_types.validation.name_required', 'El nombre es obligatorio');
    }
    if (value.trim().length < 3) {
      return translate('resources.tag_types.validation.name_min_length', 'Debe tener al menos 3 caracteres');
    }
    return undefined;
  };

  /**
   * Valida el campo 'Description'.
   * - Es opcional, pero si existe, tiene una longitud máxima.
   */
  const validateDescription = (value?: string): string | undefined => {
    if (value && value.length > 500) {
      return translate('resources.tag_types.validation.description_max_length', 'La descripción no puede exceder los 500 caracteres');
    }
    return undefined;
  };

  /**
   * Ejecuta todas las validaciones para el formulario completo.
   * Útil para la validación síncrona de react-admin.
   */
  const validateForm = (data: TagTypeFormData): TagTypeValidationErrors => {
    const errors: TagTypeValidationErrors = {};
    
    const typeCodeError = validateTypeCode(data.typeCode);
    if (typeCodeError) errors.typeCode = typeCodeError;
    
    const nameError = validateName(data.name);
    if (nameError) errors.name = nameError;

    const descriptionError = validateDescription(data.description);
    if (descriptionError) errors.description = descriptionError;

    return errors;
  };

  return { 
    validateTypeCode, 
    validateName, 
    validateDescription,
    validateForm 
  };
};